import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Bot, Workflow, History, Wallet, Inbox, Coins, BookOpen,
  LogOut, ArrowLeft, UserCog, PanelLeftClose, PanelLeftOpen, Menu, X,
} from 'lucide-react';
import { cn, formatUSD } from '../lib/utils';
import { approvalsService } from '../services/approvalsService';
import { budgetService } from '../services/budgetService';
import { usageService, type Wallet as WalletData } from '../services/usageService';
import { useAuth } from '../auth/AuthContext';
import { agentStudioPath } from '../lib/routes';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

const nav = [
  { to: agentStudioPath(), label: 'Dashboard', icon: LayoutDashboard, permission: P.dashboardView, end: true, group: 'Workspace' },
  { to: agentStudioPath('activities'), label: 'Minhas atividades', icon: Inbox, permission: P.activitiesView, badgeKey: 'approvals', group: 'Workspace' },
  { to: agentStudioPath('agents'), label: 'Agentes', icon: Bot, permission: P.agentsView, group: 'Construir' },
  { to: agentStudioPath('flows'), label: 'Fluxos', icon: Workflow, permission: P.flowsView, group: 'Construir' },
  { to: agentStudioPath('knowledge-bases'), label: 'Bases de conhecimento', icon: BookOpen, permission: P.knowledgeBasesView, group: 'Construir' },
  { to: agentStudioPath('runs'), label: 'Execuções', icon: History, permission: P.runsView, group: 'Gerenciar' },
  { to: agentStudioPath('budget'), label: 'Áreas & Budget', icon: Wallet, permission: P.budgetView, group: 'Gerenciar' },
  { to: agentStudioPath('users'), label: 'Usuários', icon: UserCog, permission: P.usersManage, group: 'Gerenciar' },
  { to: agentStudioPath('costs'), label: 'Custos', icon: Coins, permission: P.costsView, group: 'Gerenciar' },
] as const;
const groups = ['Workspace', 'Construir', 'Gerenciar'];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { has } = useAgentStudioPermissions();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const canViewActivities = has(P.activitiesView);

  useEffect(() => {
    let active = true;
    if (canViewActivities) {
      Promise.all([
        approvalsService.pendingCountForUser().catch(() => 0),
        budgetService.listPendingGrants().then((g) => g.length).catch(() => 0),
      ]).then(([a, g]) => { if (active) setPending(a + g); });
    } else {
      setPending(0);
    }
    usageService.wallet().then((next) => { if (active) setWallet(next); }).catch(() => { if (active) setWallet(null); });
    return () => { active = false; };
  }, [location.pathname, canViewActivities, user?.guid]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileOpen]);

  const displayName = user?.name ?? 'Usuário';
  const initials = displayName.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  const compact = collapsed && !mobileOpen;

  return (
    <>
      <div className="studio-mobile-bar">
        <button type="button" onClick={() => setMobileOpen(true)} aria-label="Abrir navegação" aria-expanded={mobileOpen} aria-controls="studio-navigation">
          <Menu size={22} />
        </button>
        <span>Agent <strong>Studio</strong></span>
        <span className="studio-mobile-avatar" title={displayName}>{initials || 'U'}</span>
      </div>
      {mobileOpen && <button className="studio-nav-backdrop" aria-label="Fechar navegação" onClick={() => setMobileOpen(false)} />}
      <aside id="studio-navigation" className={cn('studio-sidebar', compact && 'is-collapsed', mobileOpen && 'is-mobile-open')} aria-label="Navegação do Agent Studio">
        <div className="studio-brand">
          {!compact && <img src="/media/logos/logo-agent-studio-wide.png" alt="Compra Agora Agent Studio" />}
          <button
            type="button"
            onClick={() => mobileOpen ? setMobileOpen(false) : setCollapsed(!collapsed)}
            className="studio-collapse-button"
            aria-label={mobileOpen ? 'Fechar navegação' : compact ? 'Expandir menu' : 'Recolher menu'}
            aria-expanded={!compact}
            title={compact ? 'Expandir menu' : 'Recolher menu'}
          >
            {mobileOpen ? <X size={20} /> : compact ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <Link to="/home" title="Voltar ao Sistema Interno" className="studio-back-link" aria-label={compact ? 'Voltar ao Sistema Interno' : undefined}>
          <ArrowLeft size={16} />
          {!compact && <span>Sistema Interno</span>}
        </Link>

        <nav className="studio-nav" aria-label="Menu principal">
          {groups.map((group) => {
            const items = nav.filter((item) => item.group === group && has(item.permission));
            if (!items.length) return null;
            return (
              <div className="studio-nav-group" key={group}>
                {!compact && <div className="studio-nav-heading">{group}</div>}
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={'end' in item ? item.end : false}
                    title={compact ? item.label : undefined}
                    aria-label={compact ? item.label : undefined}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => cn('studio-nav-link', isActive && 'is-active')}
                  >
                    <item.icon size={19} className="shrink-0" />
                    {!compact && <span className="min-w-0 flex-1">{item.label}</span>}
                    {!compact && 'badgeKey' in item && pending > 0 && (
                      <span className="studio-nav-badge">{pending}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {wallet && !compact && (
          <div className="studio-wallet">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-xs text-white/70"><Coins size={15} /> Seu saldo</span>
              <strong className={cn('text-sm tabular-nums', wallet.available <= 0 ? 'text-red-300' : 'text-white')}>{formatUSD(wallet.available)}</strong>
            </div>
            <div className="my-2.5 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className={cn('h-full rounded-full', wallet.available <= 0 ? 'bg-brand-orange' : 'bg-success')}
                style={{ width: `${wallet.monthlyBudget > 0 ? Math.min(100, Math.round((wallet.monthlyRemaining / wallet.monthlyBudget) * 100)) : (wallet.available > 0 ? 100 : 0)}%` }}
              />
            </div>
            <p className="text-[11px] leading-relaxed text-white/60">
              Gasto no mês: {formatUSD(wallet.monthlySpent)}<br />
              Restante: {formatUSD(wallet.monthlyRemaining)} / {formatUSD(wallet.monthlyBudget)}
              {wallet.topup > 0 ? ` · Extra: ${formatUSD(wallet.topup)}` : ''}
            </p>
          </div>
        )}

        <div className="studio-account">
          {!compact && <>
            <div className="studio-avatar">{initials || 'U'}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold">{displayName}</div>
              <div className="mt-1 truncate text-[11px] text-white/60">{user?.email ?? ''}</div>
            </div>
          </>}
          <button type="button" onClick={() => logout()} title="Sair" aria-label="Sair" className="studio-collapse-button">
            <LogOut size={18} />
          </button>
        </div>
      </aside>
    </>
  );
}
